import { identity } from '../utils/functions';

declare const brandProperty: unique symbol;
declare const typeProperty: unique symbol;

/**
 * Nominal typing emulation for Typescript
 *
 * Useful for placing type guards for example on entity IDs, which are all strings,
 * but definitely not interchangeable between each other, or strings in specific format.
 *
 * Of course some type assertions must be done when you instantiate such type, but
 * the rest of the code will have another level of type safety. And there is a helper
 * function `brand`, that allows elevating to nominal type while checking type compatibility
 * with original type (in other words, `brand<UserId>(nonString)` will fail type checks).
 *
 * @example
 * type UserId = Branded<string, 'userId'>;
 *
 * const loadCurrentUserId = async (): UserId => brand<UserId>(await load('/user/current'));
 * const loadUserData = async (id: UserId) => { ... };
 *
 * // OK
 * loadUserData(await loadCurrentUserId());
 * // @ts-expect-error you cannot accidentally use other string in place of user id
 * loadUserData('NotAnUserId');
 *
 * // Composition is supported to build "subtypes"
 * type AdminId = Branded<string, 'adminId'> & UserId;
 * const isAdmin(userId: UserId): userId is AdminId => userId.startsWith('admin#');
 */
export type Branded<TType, TBrand extends string | symbol> = TType & {
  [brandProperty]: {
    [key in TBrand]: true;
  };
  // type incorporated in hidden property to be easily picked by brand function's first argument
  [typeProperty]: TType;
};

export const brand: <
  TBranded extends Branded<unknown, string | symbol> = never,
>(
  item: TBranded[typeof typeProperty],
) => NoInfer<TBranded> = identity as any;

export const brandArray: <
  TBranded extends Branded<unknown, string | symbol> = never,
>(
  item: TBranded[typeof typeProperty][],
) => NoInfer<TBranded>[] = identity as any;
